import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Play, Clock, Sparkles } from 'lucide-react'

const templates = [
  {
    id: 'explainer',
    name: 'Explainer Video',
    description: 'Break down complex topics with clear visuals and narration.',
    duration: '60-90 sec',
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
    popular: true,
  },
  {
    id: 'product-demo',
    name: 'Product Demo',
    description: 'Showcase your product features and benefits in action.',
    duration: '30-60 sec',
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop',
    popular: true,
  },
  {
    id: 'social-ad',
    name: 'Social Media Ad',
    description: 'Eye-catching short-form content for social platforms.',
    duration: '15-30 sec',
    thumbnail: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=225&fit=crop',
    popular: false,
  },
  {
    id: 'tutorial',
    name: 'Tutorial / How-To',
    description: 'Step-by-step instructional content with visual guides.',
    duration: '2-5 min',
    thumbnail: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=225&fit=crop',
    popular: false,
  },
  {
    id: 'testimonial',
    name: 'Customer Testimonial',
    description: 'Highlight customer success stories and reviews.',
    duration: '30-60 sec',
    thumbnail: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&h=225&fit=crop',
    popular: false,
  },
  {
    id: 'announcement',
    name: 'Announcement',
    description: 'Share news, updates, or launches with impact.',
    duration: '15-30 sec',
    thumbnail: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=225&fit=crop',
    popular: false,
  },
  {
    id: 'brand-story',
    name: 'Brand Story',
    description: 'Tell your company story and connect with your audience.',
    duration: '90-120 sec',
    thumbnail: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=225&fit=crop',
    popular: true,
  },
  {
    id: 'promo',
    name: 'Promotional Video',
    description: 'Drive action with compelling offers and CTAs.',
    duration: '15-30 sec',
    thumbnail: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=400&h=225&fit=crop',
    popular: false,
  },
]

export default function TemplatesPage() {
  return (
    <div className="p-6">
      <Helmet>
        <title>Templates - Footage</title>
        <meta name="description" content="Browse and use video templates to jumpstart your films." />
      </Helmet>
      <div className="max-w-5xl">
        <h1 className="text-2xl font-bold text-neutral-900">Templates</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Start with a template to create your video faster.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {templates.map((template) => (
            <Link
              key={template.id}
              to={`/video/new?template=${template.id}`}
              className="group bg-white rounded-xl border border-neutral-200 overflow-hidden hover:border-neutral-300 hover:shadow-lg transition-all"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video overflow-hidden bg-neutral-100">
                <img
                  src={template.thumbnail}
                  alt={template.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                    <Play className="h-5 w-5 text-neutral-900 ml-0.5" />
                  </div>
                </div>
                {template.popular && (
                  <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                    <Sparkles className="h-3 w-3" />
                    <span className="text-xs font-medium">Popular</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-semibold text-neutral-900 group-hover:text-teal-600 transition-colors">
                  {template.name}
                </h3>
                <p className="text-sm text-neutral-500 mt-1 line-clamp-2">
                  {template.description}
                </p>
                <div className="flex items-center gap-1.5 mt-3 text-xs text-neutral-400">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{template.duration}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
